import { useState, useEffect, useCallback } from 'react';
import { purchaseOrdersAPI, vendorsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, ShoppingCart, IndianRupee, Trash2, Check, Send, PackageCheck, FileText } from 'lucide-react';

const STATUSES = ['draft', 'approved', 'sent', 'received', 'cancelled'];
const STATUS_COLOR = {
  draft: 'bg-slate-100 text-slate-700',
  approved: 'bg-blue-50 text-blue-700',
  sent: 'bg-amber-50 text-amber-700',
  received: 'bg-emerald-50 text-emerald-700',
  cancelled: 'bg-red-50 text-red-700',
};

const PurchaseOrdersPage = () => {
  const { hospital, user } = useAuth();
  const hospitalId = hospital?.id;
  const [pos, setPos] = useState([]);
  const [summary, setSummary] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = hospitalId ? { hospital_id: hospitalId } : {};
      if (filterStatus !== 'all') params.status = filterStatus;
      const [r1, r2] = await Promise.all([purchaseOrdersAPI.list(params), purchaseOrdersAPI.summary(hospitalId ? { hospital_id: hospitalId } : {})]);
      setPos(r1.data || []);
      setSummary(r2.data || {});
    } catch { toast.error('Failed to load'); }
  }, [hospitalId, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const action = async (id, type) => {
    try {
      const userName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim();
      if (type === 'approve') await purchaseOrdersAPI.approve(id, { approved_by: userName });
      else if (type === 'send') await purchaseOrdersAPI.send(id);
      else if (type === 'receive') await purchaseOrdersAPI.receive(id, {});
      toast.success(`PO ${type}d`);
      load();
    } catch { toast.error('Action failed'); }
  };

  return (
    <div className="space-y-5" data-testid="purchase-orders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Purchase Orders</h1>
          <p className="text-sm text-slate-500 mt-0.5">{summary.total || 0} POs · ₹{(summary.total_value || 0).toLocaleString()} total value</p>
        </div>
        <Button onClick={() => { setEditItem(null); setShowModal(true); }} className="bg-teal-600 hover:bg-teal-700" data-testid="new-po-btn">
          <Plus size={16} className="mr-1.5" /> New PO
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <button onClick={() => setFilterStatus('all')} className={`rounded-xl border p-4 text-left transition ${filterStatus === 'all' ? 'border-teal-500 bg-teal-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}>
          <p className="text-xs text-slate-500 uppercase font-semibold">Total</p>
          <p className="text-2xl font-bold text-slate-800">{summary.total || 0}</p>
        </button>
        {STATUSES.slice(0, 4).map(s => (
          <button key={s} onClick={() => setFilterStatus(filterStatus === s ? 'all' : s)} className={`rounded-xl border p-4 text-left transition capitalize ${filterStatus === s ? 'border-teal-500 bg-teal-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}>
            <p className="text-xs text-slate-500 uppercase font-semibold">{s}</p>
            <p className="text-2xl font-bold text-slate-800">{summary[s] || 0}</p>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">PO #</th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Vendor</th>
              <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Items</th>
              <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Amount</th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Delivery</th>
              <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Status</th>
              <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pos.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-12 text-slate-400">No purchase orders.</td></tr>
            ) : pos.map(po => (
              <tr key={po.id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`po-row-${po.id}`}>
                <td className="py-3 px-4 font-mono text-xs text-slate-700">{po.po_number}</td>
                <td className="py-3 px-4 font-medium text-slate-800">{po.vendor_name}</td>
                <td className="py-3 px-4 text-center text-slate-600">{(po.items || []).length}</td>
                <td className="py-3 px-4 text-right font-semibold flex items-center justify-end gap-0.5"><IndianRupee size={12} />{po.total_amount?.toLocaleString()}</td>
                <td className="py-3 px-4 text-slate-600">{po.delivery_date || '—'}</td>
                <td className="py-3 px-4 text-center">
                  <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${STATUS_COLOR[po.status] || 'bg-slate-100'}`}>{po.status}</span>
                </td>
                <td className="py-3 px-4 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {po.status === 'draft' && <button onClick={() => action(po.id, 'approve')} title="Approve" className="p-1.5 rounded-lg hover:bg-blue-50" data-testid={`approve-${po.id}`}><Check size={14} className="text-blue-500" /></button>}
                    {po.status === 'approved' && <button onClick={() => action(po.id, 'send')} title="Send" className="p-1.5 rounded-lg hover:bg-amber-50"><Send size={14} className="text-amber-500" /></button>}
                    {po.status === 'sent' && <button onClick={() => action(po.id, 'receive')} title="Mark Received" className="p-1.5 rounded-lg hover:bg-emerald-50"><PackageCheck size={14} className="text-emerald-500" /></button>}
                    <button onClick={() => { setEditItem(po); setShowModal(true); }} title="View" className="p-1.5 rounded-lg hover:bg-slate-100"><FileText size={14} className="text-slate-400" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={showModal} onClose={() => setShowModal(false)} editItem={editItem} hospitalId={hospitalId} userName={`${user?.first_name || ''} ${user?.last_name || ''}`.trim()} onSaved={load} />
    </div>
  );
};

const Modal = ({ open, onClose, editItem, hospitalId, userName, onSaved }) => {
  const [vendors, setVendors] = useState([]);
  const [form, setForm] = useState({ vendor_id: '', items: [{ name: '', quantity: 1, unit_price: 0 }], tax_rate: 18, delivery_date: '', payment_terms: 'Net 30', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    vendorsAPI.list().then(r => setVendors(r.data || [])).catch(() => {});
    if (editItem) {
      setForm({
        vendor_id: editItem.vendor_id || '',
        items: editItem.items || [{ name: '', quantity: 1, unit_price: 0 }],
        tax_rate: editItem.tax_rate || 18,
        delivery_date: editItem.delivery_date || '',
        payment_terms: editItem.payment_terms || 'Net 30',
        notes: editItem.notes || '',
      });
    } else {
      setForm({ vendor_id: '', items: [{ name: '', quantity: 1, unit_price: 0 }], tax_rate: 18, delivery_date: '', payment_terms: 'Net 30', notes: '' });
    }
  }, [editItem, open]);

  const subtotal = form.items.reduce((s, it) => s + (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0), 0);
  const tax = subtotal * (parseFloat(form.tax_rate) || 0) / 100;
  const total = subtotal + tax;

  const updateItem = (idx, field, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: value };
    setForm({ ...form, items });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, { name: '', quantity: 1, unit_price: 0 }] });
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const save = async () => {
    if (!form.vendor_id) { toast.error('Select vendor'); return; }
    if (form.items.length === 0) { toast.error('Add at least one item'); return; }
    setSaving(true);
    try {
      const vendor = vendors.find(v => v.id === form.vendor_id);
      const payload = { ...form, hospital_id: hospitalId, vendor_name: vendor?.name || '', created_by: userName, items: form.items.map(it => ({ ...it, quantity: parseFloat(it.quantity) || 0, unit_price: parseFloat(it.unit_price) || 0 })) };
      if (editItem) await purchaseOrdersAPI.update(editItem.id, payload);
      else await purchaseOrdersAPI.create(payload);
      toast.success('Saved'); onSaved(); onClose();
    } catch { toast.error('Failed'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editItem ? `PO ${editItem.po_number}` : 'New Purchase Order'}</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Vendor *</Label>
              <Select value={form.vendor_id} onValueChange={v => setForm({ ...form, vendor_id: v })}>
                <SelectTrigger data-testid="po-vendor-select"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                <SelectContent>{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Delivery Date</Label><Input type="date" value={form.delivery_date} onChange={e => setForm({ ...form, delivery_date: e.target.value })} /></div>
            <div><Label>Tax Rate %</Label><Input type="number" value={form.tax_rate} onChange={e => setForm({ ...form, tax_rate: e.target.value })} /></div>
            <div><Label>Payment Terms</Label><Input value={form.payment_terms} onChange={e => setForm({ ...form, payment_terms: e.target.value })} /></div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Items</Label>
              <Button size="sm" variant="outline" onClick={addItem} data-testid="add-po-item-btn"><Plus size={14} className="mr-1" /> Add</Button>
            </div>
            <div className="space-y-2">
              {form.items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-6"><Input placeholder="Item description" value={it.name} onChange={e => updateItem(i, 'name', e.target.value)} /></div>
                  <div className="col-span-2"><Input type="number" placeholder="Qty" value={it.quantity} onChange={e => updateItem(i, 'quantity', e.target.value)} /></div>
                  <div className="col-span-3"><Input type="number" placeholder="Unit ₹" value={it.unit_price} onChange={e => updateItem(i, 'unit_price', e.target.value)} /></div>
                  <div className="col-span-1"><button onClick={() => removeItem(i)} className="p-1.5 rounded-lg hover:bg-red-50"><Trash2 size={14} className="text-red-400" /></button></div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-slate-50 rounded-lg p-4 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span>₹{subtotal.toLocaleString()}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Tax ({form.tax_rate}%)</span><span>₹{tax.toLocaleString()}</span></div>
            <div className="flex justify-between font-bold text-base border-t border-slate-200 pt-2 mt-1"><span>Total</span><span>₹{total.toLocaleString()}</span></div>
          </div>

          <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-po-btn">{saving ? 'Saving...' : 'Save PO'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PurchaseOrdersPage;
